<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Modal from '../../../../components/Modal.vue';

export default {
  components: {
    NextButton,
    Modal,
    WootMessageEditor,
  },
  props: {
    id: { type: Number, default: null },
    edcontent: { type: String, default: '' },
    edshortCode: { type: String, default: '' },
    edAttachmentUrl: { type: String, default: '' },
    edAttachmentContentType: { type: String, default: '' },
    onClose: { type: Function, default: () => {} },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      editCanned: {
        showAlert: false,
        showLoading: false,
      },
      shortCode: this.edshortCode,
      content: this.edcontent,
      newAttachment: null,
      newAttachmentPreview: null,
      existingAttachmentUrl: this.edAttachmentUrl || '',
      existingAttachmentType: this.edAttachmentContentType || '',
      removeAttachmentFlag: false,
      show: true,
    };
  },
  computed: {
    pageTitle() {
      return `${this.$t('CANNED_MGMT.EDIT.TITLE')} - ${this.edshortCode}`;
    },
    hasAttachment() {
      return (
        !!this.newAttachment ||
        (!!this.existingAttachmentUrl && !this.removeAttachmentFlag)
      );
    },
    isSubmitDisabled() {
      if (this.v$.shortCode.$invalid || this.editCanned.showLoading)
        return true;
      if (!this.content && !this.hasAttachment) return true;
      return false;
    },
    newAttachmentIcon() {
      if (!this.newAttachment) return null;
      const type = this.newAttachment.type;
      if (type.startsWith('image/')) return 'i-lucide-image';
      if (type.startsWith('video/')) return 'i-lucide-video';
      if (type.startsWith('audio/')) return 'i-lucide-audio-lines';
      return 'i-lucide-file';
    },
    existingAttachmentIcon() {
      if (!this.existingAttachmentType) return 'i-lucide-file';
      const type = this.existingAttachmentType;
      if (type.startsWith('image/')) return 'i-lucide-image';
      if (type.startsWith('video/')) return 'i-lucide-video';
      if (type.startsWith('audio/')) return 'i-lucide-audio-lines';
      return 'i-lucide-file';
    },
  },
  validations: {
    shortCode: {
      required,
      minLength: minLength(2),
    },
  },
  methods: {
    onFileChange(e) {
      const file = e.target.files[0];
      if (!file) return;
      this.newAttachment = file;
      this.removeAttachmentFlag = false;
      if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
        this.newAttachmentPreview = URL.createObjectURL(file);
      } else {
        this.newAttachmentPreview = null;
      }
    },
    removeNewAttachment() {
      this.newAttachment = null;
      this.newAttachmentPreview = null;
      this.$refs.fileInput.value = '';
    },
    removeExistingAttachment() {
      this.existingAttachmentUrl = '';
      this.removeAttachmentFlag = true;
    },
    resetForm() {
      this.shortCode = '';
      this.content = '';
      this.newAttachment = null;
      this.newAttachmentPreview = null;
      this.v$.shortCode.$reset();
    },
    editCannedResponse() {
      this.editCanned.showLoading = true;
      const payload = {
        id: this.id,
        short_code: this.shortCode,
        content: this.content,
      };
      if (this.newAttachment) {
        payload.attachment = this.newAttachment;
      } else if (this.removeAttachmentFlag) {
        payload.remove_attachment = true;
      }
      this.$store
        .dispatch('updateCannedResponse', payload)
        .then(() => {
          this.editCanned.showLoading = false;
          useAlert(this.$t('CANNED_MGMT.EDIT.API.SUCCESS_MESSAGE'));
          this.resetForm();
          setTimeout(() => {
            this.onClose();
          }, 10);
        })
        .catch(error => {
          this.editCanned.showLoading = false;
          const errorMessage =
            error?.message || this.$t('CANNED_MGMT.EDIT.API.ERROR_MESSAGE');
          useAlert(errorMessage);
        });
    },
  },
};
</script>

<template>
  <Modal v-model:show="show" :on-close="onClose">
    <div class="flex flex-col h-auto overflow-auto">
      <woot-modal-header :header-title="pageTitle" />
      <form class="flex flex-col w-full" @submit.prevent="editCannedResponse()">
        <div class="w-full">
          <label :class="{ error: v$.shortCode.$error }">
            {{ $t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.LABEL') }}
            <input
              v-model="shortCode"
              type="text"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.SHORT_CODE.PLACEHOLDER')"
              @input="v$.shortCode.$touch"
            />
          </label>
        </div>

        <div class="w-full">
          <label>
            {{ $t('CANNED_MGMT.EDIT.FORM.CONTENT.LABEL') }}
            <span class="text-n-slate-9 text-sm ml-1">({{ $t('CANNED_MGMT.OPTIONAL') }})</span>
          </label>
          <div class="editor-wrap">
            <WootMessageEditor
              v-model="content"
              class="message-editor [&>div]:px-1"
              channel-type="Context::Default"
              enable-variables
              :enable-canned-responses="false"
              :placeholder="$t('CANNED_MGMT.EDIT.FORM.CONTENT.PLACEHOLDER')"
            />
          </div>
        </div>

        <!-- Media attachment section -->
        <div class="w-full mt-3">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ $t('CANNED_MGMT.EDIT.FORM.MEDIA.LABEL') }}
            <span class="text-n-slate-9 text-sm ml-1">({{ $t('CANNED_MGMT.OPTIONAL') }})</span>
          </label>

          <!-- New attachment being added -->
          <div
            v-if="newAttachment"
            class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak mb-2"
          >
            <img
              v-if="
                newAttachmentPreview && newAttachment.type.startsWith('image/')
              "
              :src="newAttachmentPreview"
              class="h-16 w-16 object-cover rounded"
              alt="preview"
            />
            <video
              v-else-if="
                newAttachmentPreview && newAttachment.type.startsWith('video/')
              "
              :src="newAttachmentPreview"
              class="h-16 w-16 object-cover rounded"
            />
            <div
              v-else
              class="h-16 w-16 flex items-center justify-center rounded bg-n-alpha-2"
            >
              <span
                class="size-8 text-n-slate-10"
                :class="[newAttachmentIcon]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ newAttachment.name }}
              </p>
              <p class="text-xs text-n-slate-9">{{ newAttachment.type }}</p>
            </div>
            <button
              type="button"
              class="p-1 rounded text-n-slate-9 hover:text-ruby-9"
              @click="removeNewAttachment"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Existing attachment -->
          <div
            v-else-if="existingAttachmentUrl && !removeAttachmentFlag"
            class="flex items-center gap-3 p-3 rounded-lg bg-n-alpha-1 border border-n-weak mb-2"
          >
            <img
              v-if="
                existingAttachmentType &&
                existingAttachmentType.startsWith('image/')
              "
              :src="existingAttachmentUrl"
              class="h-16 w-16 object-cover rounded"
              alt="preview"
            />
            <div
              v-else
              class="h-16 w-16 flex items-center justify-center rounded bg-n-alpha-2"
            >
              <span
                class="size-8 text-n-slate-10"
                :class="[existingAttachmentIcon]"
              />
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12">
                {{ $t('CANNED_MGMT.CURRENT_ATTACHMENT') }}
              </p>
              <p class="text-xs text-n-slate-9">{{ existingAttachmentType }}</p>
            </div>
            <div class="flex gap-2">
              <button
                type="button"
                class="p-1 rounded text-n-slate-9 hover:text-n-brand"
                @click="$refs.fileInput.click()"
              >
                <span class="i-lucide-pencil size-4" />
              </button>
              <button
                type="button"
                class="p-1 rounded text-n-slate-9 hover:text-ruby-9"
                @click="removeExistingAttachment"
              >
                <span class="i-lucide-x size-4" />
              </button>
            </div>
          </div>

          <!-- No attachment -->
          <div v-else>
            <button
              type="button"
              class="flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-dashed border-n-weak text-n-slate-10 hover:border-n-brand hover:text-n-brand transition-colors"
              @click="$refs.fileInput.click()"
            >
              <span class="i-lucide-paperclip size-4" />
              {{ $t('CANNED_MGMT.EDIT.FORM.MEDIA.BUTTON') }}
            </button>
          </div>

          <!-- Single hidden file input shared by all triggers -->
          <input
            ref="fileInput"
            type="file"
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx"
            class="hidden"
            @change="onFileChange"
          />
        </div>

        <div class="flex flex-row justify-end w-full gap-2 px-0 py-2 mt-2">
          <NextButton
            faded
            slate
            type="reset"
            :label="$t('CANNED_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('CANNED_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="isSubmitDisabled"
            :is-loading="editCanned.showLoading"
          />
        </div>
      </form>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
::v-deep {
  .ProseMirror-menubar {
    @apply hidden;
  }

  .ProseMirror-woot-style {
    @apply min-h-[8rem];

    p {
      @apply text-base;
    }
  }
}
</style>
